/** @jsxRuntime classic */
/** @jsx jsx */

import React, { useEffect, useRef, useState } from 'react';
import type { Ref } from 'react';
import styled from '@emotion/styled';
import { jsx, css } from '@emotion/core';
import { Link, Colors, Icon } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { useScreenSize } from 'hooks/useScreenSize';
import { NavDropdown } from './NavDropdown';
import { Submenu as SubmenuLinks } from './Submenu';
import type { NavItemType, NavSize, OnChooseItem } from './types';

export type NavRefElement = HTMLDivElement;

export type Props = {
  /**
   * Optional label that provides context as to what the navigation is used for.
   * Used by screen reading software.
   */
  ariaLabel?: string;
  /**
   * React ref that is forwarded along as a prop to facilitate
   * typing and to eliminate use of `forwardRef`.
   */
  forwardedRef?: Ref<NavRefElement>;
  /**
   * An array of items to render within the navigation.
   */
  items: Array<NavItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * The `href` of the currently selected navigation item. `href` is
   * used rather than the full item since the item object could be
   * cloned, which ruins `===` checks.
   */
  selectedItemHref: string;
  /**
   * Optional value that will determine if the nav item should be highlighted
   * or not. When not provided, the `href` will be compared to the
   * `selectedItemHref` passed to `Nav`.
   */
  isItemSelected: (item: NavItemType, selectedItemHref: string) => boolean;
  /**
   * Size of navigation. `medium` typically used for small screens
   * and `large` typically used for larger screens.
   */
  size: NavSize;
  /**
   * Whether or not the component should render the grey indicator track beneath the nav items.
   */
  shouldRenderTrack: boolean;
  /**
   * Whether or not underline the selected item in the navigation.
   */
  underlineSelectedItem: boolean;
  /**
   * Custom Link component to render in the Navigation items.
   */
  linkComponent?: React.ElementType;
};

const indicatorTrackHeight = 3;

const Styles = labelStyles('Nav', {
  root: (size: NavSize) => [
    {
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      columnGap: 20,
      rowGap: 2,
      alignItems: 'center',
    },

    size === 'medium' &&
      withModifier('medium', {
        height: 60,
      }),

    size === 'large' &&
      withModifier('large', {
        height: 60,
      }),
  ],

  primary: ({ count }: { count: number }) => [
    {
      display: 'grid',
      height: '100%',
      // Allow shrinking
      minWidth: 0,
      maxWidth: '100%',
    },

    withModifier(`count-${count}`, {
      // `minmax(auto, min-content)` behaves such that at the min value of
      // `auto` items could truncate if necessary, and at the max value of
      // `min-content` items will not render any wider than they need to,
      // ensuring that they are left-aligned rather than stretching to fill
      // any leftover space.
      gridTemplateColumns: `repeat(${count}, minmax(auto, min-content))`,
      gap: '25px',
    }),
  ],

  item: (isSelected: boolean) => [
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 0 10px;
      line-height: 20px;
    `,
    isSelected &&
      withModifier('selected', {
        color: Colors.black,
        fontWeight: 700,
      }),
  ],
  indicatorTrack: (props: Props) => [
    {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      // This ends up being a non-integer, but it actually renders better
      // in Chrome (and still renders fine in other browsers) this way.
      borderRadius: indicatorTrackHeight / 2,
      height: indicatorTrackHeight,
      overflow: 'hidden',
    },
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    props.shouldRenderTrack &&
      withModifier('visible', {
        backgroundColor: Colors.gray10,
      }),
  ],

  indicator: (props: Props) => [
    {
      position: 'absolute',
      height: 'inherit',
      borderRadius: 'inherit',
      backgroundColor: props.underlineSelectedItem ? Colors.ocean : 'initial',
      transition: 'all 250ms ease-in-out',
    },
  ],
});

type NavItemRefElement = HTMLAnchorElement | HTMLDivElement;

type NavItemProps = NavItemType & {
  onChoose: (href: string, event: React.MouseEvent) => void;
  forwardedRef: Ref<NavItemRefElement> | null;
  isSelected: boolean;
};

// Since this is a component rather than a DOM node, `forwardRef`
// is used to facilitate properly passing the `ref` along to the DOM node.
const NavItem = (props: NavItemProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isMobile, isTablet } = useScreenSize();

  const openMenu = () => setIsMenuOpen(true);
  const closeMenu = () => setIsMenuOpen(false);

  const toggleDetails = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    /**
     * This is needed to prevent default `details` behavior
     * Reference - https://github.com/facebook/react/issues/15486#issuecomment-488028431.
     */
    e.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  const isNotDesktop = isMobile || isTablet;

  // I was getting a `JSX element type Elem does not have any construct or call signatures` Typescript error.
  // Fixed it with: https://stackoverflow.com/a/60323737/1405803
  const LinkComponent = props.linkComponent as React.ElementType;

  /**
   * Render a regular link as it doesn't have any children
   */
  if (!props.submenu?.length) {
    return (
      <div css={Styles.item(props.isSelected)}>
        <LinkComponent
          ariaLabel={props.isSelected ? `${props.text}, selected` : props.text}
          href={props.href}
          onClick={(e: React.MouseEvent) => {
            props.onChoose(props.href, e);
          }}
          innerRef={props.forwardedRef as Ref<HTMLAnchorElement>}
        >
          <LinkText isSelected={props.isSelected}>{props.text}</LinkText>
        </LinkComponent>
      </div>
    );
  }

  /**
   * Render a link with a Submenu
   */
  return (
    <Details
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
      onClick={isNotDesktop ? toggleDetails : undefined}
      open={isMenuOpen}
      isSelected={props.isSelected}
    >
      <Summary
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        role="button"
        tabIndex={0}
      >
        <SummaryInnerText>
          <ItemText isSelected={props.isSelected}>{props.text}</ItemText>
          <Caret>
            <Icon isBlock={true} name="arrowSmallDown" size={16} />
          </Caret>
        </SummaryInnerText>
      </Summary>
      <Submenu onClick={isNotDesktop ? closeMenu : undefined} role="menu">
        <SubmenuLinks links={props.submenu} onChoose={props.onChoose} />
      </Submenu>
    </Details>
  );
};

/**
 * Creates a horizontal navigation component that can display a variable
 * number of items, and allows excess items to be nested into a dropdown menu.
 */
export const Nav = (props: Props) => {
  // Hold onto ref so the selected node can be measured
  // in order to position `indicator` via `indicatorStyles`.
  const selectedItemRef = useRef<NavItemRefElement>(null);
  const [indicatorStyles, setIndicatorStyles] = useState({});

  const shouldRenderIndicator = !props.items.every((i) => i.isExtra);

  const primaryItems = props.items.filter((item) => !item.isExtra);
  const primaryLinks = primaryItems.map((item) => {
    const isSelected = props.isItemSelected(item, props.selectedItemHref);
    return (
      <NavItem
        {...item}
        forwardedRef={
          isSelected && shouldRenderIndicator ? selectedItemRef : null
        }
        isSelected={isSelected}
        key={item.text}
        onChoose={(href, event) => {
          props.onChooseItem(
            primaryItems.find(
              (navItem) =>
                navItem.href === href ||
                navItem.submenu?.some(
                  (subMenuItem) => subMenuItem.href === href,
                ),
            ) as NavItemType,
            event,
          );
        }}
        linkComponent={props.linkComponent}
      />
    );
  });

  const secondaryItems = props.items.filter((item) => item.isExtra);
  const isSecondaryItemSelected = secondaryItems.some((item) => {
    return props.isItemSelected(item, props.selectedItemHref);
  });
  const secondaryMenu = secondaryItems.length > 0 && (
    <div
      ref={
        isSecondaryItemSelected && shouldRenderIndicator
          ? (selectedItemRef as Ref<HTMLDivElement>)
          : null
      }
    >
      <NavDropdown
        items={secondaryItems}
        onChooseItem={props.onChooseItem}
        selectedItemHref={props.selectedItemHref}
        shouldIndicateSelected={shouldRenderIndicator}
        size={props.size}
      />
    </div>
  );

  // Whenever `selectedItemHref` changes, read the position of
  // the `current`ly selected node.
  useEffect(() => {
    // `requestAnimationFrame` ensures rendering has completed
    // and measuring is accurate. Without this some measurements
    // may be off on initial render.
    requestAnimationFrame(() => {
      setIndicatorStyles(
        selectedItemRef.current
          ? {
              left: selectedItemRef.current!.offsetLeft,
              width: selectedItemRef.current!.offsetWidth,
            }
          : {},
      );
    });
  }, [props.items, props.selectedItemHref]);

  return (
    <nav aria-label={props.ariaLabel} css={Styles.root(props.size)}>
      <div
        css={Styles.primary({
          count: primaryLinks.length,
        })}
        ref={props.forwardedRef}
      >
        {primaryLinks}
      </div>
      {secondaryMenu}
      {props.shouldRenderTrack && (
        <div css={Styles.indicatorTrack(props)}>
          <div css={Styles.indicator(props)} style={indicatorStyles} />
        </div>
      )}
    </nav>
  );
};

Nav.defaultProps = {
  isItemSelected: (item: NavItemType, selectedItemHref: string) =>
    item.href === selectedItemHref,
  onChooseItem: () => {},
  shouldRenderTrack: true,
  size: 'medium',
  /**
   * We default to ZDS Link component
   */
  linkComponent: Link,
};

const Caret = styled.span`
  color: ${Colors.neutral700};
  margin-left: 3px;
`;

const Summary = styled.summary`
  height: 100%;
  list-style-type: none;
  /* Hides default icon on details element */
  &::-webkit-details-marker {
    display: none;
  }
  /* Hide default icon from IE. */
  &::before {
    display: none;
  }
`;

const Submenu = styled.div`
  width: 220px;
  position: absolute;
  left: 0;
  top: 100%;
  /**
    This node needs to be at exactly "top: 100%" so that there isn't
    a gap of even 1px between it and its toggle, otherwise hovering
    off can occur in that gap and cause the menu to hide. However visually
    the node needs to be pushed down 1px so that the topbar's bottom border
    renders. Therefore we need a transparent 1px sliver that: 1) is part of
    the node and thus fills the 1px gap caused by the topbar's bottom border,
    2) is transparent so the topbar's bottom border can still be seen,
    even though it's overlapped by the node, and 3) push the content
    within this node down by 1px so that its background doesn't overlap
    the topbar's bottom border and cover it up. So there you have it,
    one line of code with a 10 line explanation 😄
  */
  border-top: 1px solid transparent;
  /**
    Pad wrapper around Submenu to clip its shadow only on the top edge.
    The shadow needs to be clipped because it's not (reasonably) possible
    to have the node render beneath the topbar when it's a child of the topbar.
    The padding values are based on the offset/blur/spread of the shadow.
    This serves a dual purpose of also increasing the hover area of the
    menu so that hovering a little bit off of it doesn't close the menu.
  */
  padding: 0 20px 20px 20px;
  /** Need to reposition the menu due to the transparent padding. */
  margin-left: -20px;
  /** Actually clip the shadow, again using padding as a sort of clip mask. */
  overflow: hidden;
  /**
  Padding should add to the width of the box in this case since it
  isn't visual. Makes it a little easier to reason about dimensions.
  */
  box-sizing: content-box;
`;

const SummaryInnerText = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
`;

const ItemText = styled.span<{ isSelected: boolean }>`
  display: block;
  white-space: nowrap;
  color: ${Colors.neutral700};
  font-size: 14px;
  font-weight: ${({ isSelected }) => {
    if (isSelected) return 600;
    return 400;
  }};
`;

const LinkText = styled(ItemText)`
  padding: 20px 0;
  &:hover,
  :focus,
  :focus-within {
    color: ${Colors.blueJeans};
  }
`;

const Details = styled.details<{ isSelected: boolean }>`
  display: flex;
  position: relative;
  &:hover,
  :focus,
  :focus-within {
    & ${ItemText}, ${Caret} {
      color: ${Colors.blueJeans};
      font-weight: ${({ isSelected }) => {
        if (isSelected) return 600;
        return 400;
      }};
    }
  }
  &:hover {
    cursor: default;
  }
`;
